
import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import PageLayout from "@/components/layout/PageLayout";
import { useToast } from "@/components/ui/use-toast";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { DashboardContent } from "@/components/dashboard/DashboardContent";
import { AdminBackBreadcrumb } from "@/components/dashboard/AdminBackBreadcrumb";
import { Spinner } from "@/components/ui/spinner";

const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const hasRedirected = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    // Set initial values for refs
    mounted.current = true;
    hasRedirected.current = false;
    
    const checkAuthAndRole = async () => {
      // Guard against multiple executions within the same render cycle
      if (hasRedirected.current) return;

      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError || !session) {
          console.error("Session error or no session:", sessionError);
          if (mounted.current && !hasRedirected.current) {
            hasRedirected.current = true;
            navigate("/auth");
          }
          return;
        }

        const { data: roleData, error: roleError } = await supabase
          .rpc('get_user_role_safely', {
            user_id_input: session.user.id
          });

        if (roleError) {
          console.error("Error checking role:", roleError);
          if (mounted.current) {
            toast({
              variant: "destructive",
              title: "Error",
              description: "Failed to check user role"
            });
          }
          return;
        }

        if (!mounted.current) return;

        // Check if user is admin OR producer and handle redirection
        const userIsAdminOrProducer = roleData === 'admin' || roleData === 'producer';
        
        // Handle admin redirection - only if we haven't already redirected
        if (userIsAdminOrProducer && 
            location.pathname === '/dashboard' && 
            !location.state?.source && 
            !hasRedirected.current) {
          console.log("Admin or Producer user detected, redirecting to admin dashboard");
          hasRedirected.current = true;
          navigate("/admin", { replace: true });
          return;
        }

        if (mounted.current && !hasRedirected.current) {
          setIsAdmin(userIsAdminOrProducer);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error in checkAuthAndRole:", error);
        if (mounted.current) {
          toast({
            variant: "destructive",
            title: "Error",
            description: "An unexpected error occurred"
          });
        }
      }
    };

    checkAuthAndRole();

    return () => {
      mounted.current = false;
    };
  }, []); // Empty dependencies array - we don't want this to re-run

  if (loading) {
    return (
      <PageLayout>
        <div className="flex justify-center items-center min-h-screen">
          <Spinner className="w-8 h-8" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout showBreadcrumbs={false}>
      <SidebarProvider>
        <div className="flex min-h-screen w-full">
          <DashboardSidebar />
          <div className="flex-1">
            {isAdmin && location.state?.source === "admin-menu" && (
              <AdminBackBreadcrumb />
            )}
            <DashboardContent 
              isAdmin={isAdmin}
              selectedClientName=""
              selectedClientId=""
            />
          </div>
        </div>
      </SidebarProvider>
    </PageLayout>
  );
};

export default DashboardPage;
